import React, { createContext, ReactNode, useCallback, useContext, useState } from "react"

interface CollapsibleSectionContextValue {
  sectionStates: Record<string, boolean>
  registerSection: (sectionId: string, openByDefault?: boolean) => void
  toggleSection: (sectionId: string) => void
  getSectionState: (sectionId: string) => boolean | undefined
}

const CollapsibleSectionContext = createContext<CollapsibleSectionContextValue | null>(
  null
)

interface CollapsibleSectionProviderProps {
  children: ReactNode
}

export function CollapsibleSectionProvider({
  children,
}: CollapsibleSectionProviderProps) {
  const [sectionStates, setSectionStates] = useState<Record<string, boolean>>({})

  const registerSection = useCallback((sectionId: string, openByDefault?: boolean) => {
    setSectionStates((prev) => {
      // Only register if not already registered
      if (typeof prev[sectionId] === "boolean") return prev
      return { ...prev, [sectionId]: Boolean(openByDefault) }
    })
  }, [])

  const toggleSection = useCallback((sectionId: string) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }, [])

  const getSectionState = useCallback(
    (sectionId: string): boolean | undefined => {
      return sectionStates[sectionId]
    },
    [sectionStates]
  )

  const value: CollapsibleSectionContextValue = {
    sectionStates,
    registerSection,
    toggleSection,
    getSectionState,
  }

  return (
    <CollapsibleSectionContext.Provider value={value}>
      {children}
    </CollapsibleSectionContext.Provider>
  )
}

export function useCollapsibleSectionState(
  sectionId: string,
  options?: { openByDefault?: boolean }
) {
  const context = useContext(CollapsibleSectionContext)

  if (!context) {
    throw new Error(
      "useCollapsibleSectionState must be used within a CollapsibleSectionProvider"
    )
  }

  const { registerSection, toggleSection, getSectionState } = context

  // Register the section with default state on first render
  React.useEffect(() => {
    registerSection(sectionId, options?.openByDefault)
  }, [sectionId, options?.openByDefault, registerSection])

  const isOpenState = getSectionState(sectionId)
  // If the section is not registered, use the default state
  const isOpen = isOpenState === undefined ? Boolean(options?.openByDefault) : isOpenState

  const toggleCollapsed = useCallback(() => {
    toggleSection(sectionId)
  }, [sectionId, toggleSection])

  return { isOpen, toggleCollapsed }
}
